import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form } from 'reactstrap';
import { TextDropZone } from '../../DropZones/TextDropZone';
import { useRetellingApi } from './RetellingApi';
import { SubmitButton } from '../shared/SubmitButton';
import { AI_UTILITIES } from '../../../constants/aiUtilities';
import { useNotification } from '../../../components/index';
import { useMountedRef } from '../../../components/hooks/useMountedRef';
import { StartTimePicker } from '../shared/StartTimePicker';
import { useRepeatProcessingLoader } from '../shared/hooks/useRepeatProcessingLoader';
import { CommentInput } from '../shared/CommentInput';
import { useUserPreferences } from '../shared/hooks/useUserPreferences';
import { DEFAULT_OUTPUT_FORMAT, OUTPUT_FORMATS } from '../shared/constants/outputFormats';

export const RetellingLoader = () => {
    const history = useHistory();
    const location = useLocation();
    const { getPreferences, savePreferences } = useUserPreferences(); 

    const [files, setFiles] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [defaultOutputFormat, setDefaultOutputFormat] = useState(DEFAULT_OUTPUT_FORMAT);
    const [defaultTemplate, setDefaultTemplate] = useState(null);

    // Режим повторной обработки
    const isRepeatMode = useRef(location.state?.isRepeatProcessing || false);
    const userInteracted = useRef(false);

    const { submitRetellingRequest } = useRetellingApi();
    const {
        showError,
        showWarning
    } = useNotification();
    const isMounted = useMountedRef();

    useEffect(() => {
        if (isRepeatMode.current) {
            return;
        }
        const loadPreferences = async () => {
            try {
                const prefs = await getPreferences(AI_UTILITIES.RETELLING.id);
                // Валидация выходного формата
                const validFormats = OUTPUT_FORMATS.map(f => f.value);
                if (prefs.defaultOutputFormat && validFormats.includes(prefs.defaultOutputFormat)) {
                    setDefaultOutputFormat(prefs.defaultOutputFormat);
                } else {
                    setDefaultOutputFormat(DEFAULT_OUTPUT_FORMAT);
                }
                // Шаблон пока не проверяем (загрузится позже в DropZoneBase)
                if (prefs.defaultTemplate) {
                    setDefaultTemplate(prefs.defaultTemplate);
                } else {
                    setDefaultTemplate(null);
                }
            } catch (error) {
                // Если предпочтений нет или ошибка – используем дефолты
                console.warn('Failed to load preferences', error);
            }
        };
        loadPreferences();
    }, []);

    useRepeatProcessingLoader({
        locationState: location.state,
        setFiles,
        setIsLoading,
        setIsInitializing,
        showError,
        isMounted,
        onLoaded: (state) => {
            setComment(state.comment || '');
        },
    });

    const saveCurrentPreferences = async () => {
        if (isRepeatMode.current && !userInteracted.current) return;
        try {
            await savePreferences(AI_UTILITIES.RETELLING.id, {
                defaultOutputFormat,
                defaultTemplate,
            });
        } catch (error) {
            console.error('Failed to save preferences', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (files.length === 0) {
            showWarning("Пожалуйста, выберите файлы");
            return;
        }

        try {
            saveCurrentPreferences();
        }
        catch (error) {
            console.error(error);
        }

        setIsLoading(true);
        const startTimeString = startTime ? startTime.toISOString() : '';
        try {
            const { id, identifier } = await submitRetellingRequest(files, comment, startTimeString);

            if (isMounted.current) {
                history.push({
                    pathname: `${AI_UTILITIES.RETELLING.path}/${identifier}`,
                    state: {
                        id: id,
                        fileNames: files.map(f => f.file.name),
                        comment: comment
                    }
                });
            }
        } catch (error) {
            showError(error.message);
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    const handleFilesChange = (newFiles) => {
        if (!isLoading && !isInitializing) {
            setFiles(newFiles);
        }
    };

    const handleFormatChange = (newFormat) => {
        setDefaultOutputFormat(newFormat);
        userInteracted.current = true;
        isRepeatMode.current = false;
    };

    const handleTemplateChange = (newTemplate) => {
        setDefaultTemplate(newTemplate);
        userInteracted.current = true;
        isRepeatMode.current = false;
    };

    return (
        <Form onSubmit={handleSubmit} className="speakers-form-container">
            <CommentInput
                id="retellingComment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isLoading || isInitializing}
            />

            <StartTimePicker
                value={startTime}
                onChange={setStartTime}
                disabled={isLoading || isInitializing}
            />

            <TextDropZone
                files={files}
                isLoading={isLoading || isInitializing}
                onFilesChange={handleFilesChange}
                expectedContent="Текстовые документы, требующие сокращения и выделения ключевых фактов"
                showFormatSelector={true}
                maxFiles={2}
                showTemplateSelector={true}
                defaultOutputFormat={defaultOutputFormat}
                defaultTemplate={defaultTemplate}
                onFormatChange={handleFormatChange}
                onTemplateChange={handleTemplateChange}
            />

            <SubmitButton
                isLoading={isLoading}
                text="Отправить на обработку ИИ"
                disabled={isLoading || isInitializing || files.length === 0}
            />
        </Form>
    );
};